import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { RefreshCw, Upload, ExternalLink } from "lucide-react";

// Map each CMS section to the public route that renders it. Used to offer
// "Preview as visitor" links next to every Save button.
const SECTION_PUBLIC_PATH = {
  homepage_hero: "/",
  homepage_services_intro: "/",
  homepage_why_choose: "/",
  homepage_cta: "/",
  homepage_zoopla: "/",
  about_page: "/about",
  about_intro: "/about",
  services_property_management: "/services/property-management",
  services_lettings: "/services/lettings",
  services_investments: "/services/investments",
  landlords_page: "/landlords",
  tenants_page: "/tenants",
  contact_info: "/contact",
  footer: "/",
};

// Pages exposed in the top "Preview as visitor" toolbar.
const PUBLIC_PAGES = [
  { label: "Home", path: "/" },
  { label: "Properties", path: "/properties" },
  { label: "Property Management", path: "/services/property-management" },
  { label: "Lettings", path: "/services/lettings" },
  { label: "Investments", path: "/services/investments" },
  { label: "Landlords", path: "/landlords" },
  { label: "Tenants", path: "/tenants" },
  { label: "About", path: "/about" },
  { label: "Contact", path: "/contact" },
  { label: "News", path: "/news" },
];

/**
 * CmsEditor — the entire "Website Content Management" tab content.
 *
 * State is owned by the parent (AdminPage) and threaded down via props so the
 * editor stays a pure presentational component.
 */
export function CmsEditor({
  siteContent,
  setSiteContent,
  handleUpdateContent,
  handleSeedDefaultContent,
  handleCMSImageUpload,
}) {
  // Generic CMS text-field helper. Updates siteContent[section][field] on change.
  // Pass `multiline` for textarea; `rows` defaults to 2.
  const CmsField = ({ section, field, label, multiline = false, rows = 2, placeholder, type = "text" }) => {
    const sectionData = siteContent[section] || {};
    const value = sectionData[field] ?? "";
    const handleChange = (e) => setSiteContent({
      ...siteContent,
      [section]: { ...sectionData, [field]: e.target.value },
    });
    return (
      <div>
        {label && <label className="block text-sm font-medium mb-1">{label}</label>}
        {multiline ? (
          <Textarea value={value} onChange={handleChange} rows={rows} placeholder={placeholder} />
        ) : (
          <Input value={value} onChange={handleChange} placeholder={placeholder} type={type} />
        )}
      </div>
    );
  };

  // JSON-array textarea editor with local draft state.
  // - Edits live in local state while the user is typing (so invalid JSON
  //   doesn't get reverted mid-keystroke).
  // - Commit-on-blur: parsed JSON is pushed to siteContent only if valid.
  // - Shows an inline "Invalid JSON" indicator while the draft is unparsable.
  const JsonListField = ({ section, field, label, hint, rows = 10 }) => {
    const sectionData = siteContent[section] || {};
    const canonical = JSON.stringify(sectionData[field] || [], null, 2);
    const [draft, setDraft] = useState(canonical);
    const [error, setError] = useState(null);

    // Reset draft when the underlying value changes externally (e.g. after save / reload).
    useEffect(() => {
      setDraft(canonical);
      setError(null);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canonical]);

    const handleChange = (e) => {
      const value = e.target.value;
      setDraft(value);
      try {
        JSON.parse(value);
        setError(null);
      } catch (err) {
        setError(err.message);
      }
    };

    const handleBlur = () => {
      try {
        const parsed = JSON.parse(draft);
        setSiteContent({
          ...siteContent,
          [section]: { ...sectionData, [field]: parsed },
        });
        setError(null);
      } catch (err) {
        setError(err.message);
      }
    };

    return (
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium">{label}</label>
          {error && (
            <span className="text-xs text-red-600 font-medium" data-testid={`json-error-${section}-${field}`}>
              Invalid JSON: {error}
            </span>
          )}
        </div>
        {hint && <p className="text-xs text-slate-500 mb-1">{hint}</p>}
        <Textarea
          value={draft}
          onChange={handleChange}
          onBlur={handleBlur}
          rows={rows}
          className={`font-mono text-xs ${error ? "border-red-400 focus-visible:ring-red-300" : ""}`}
        />
      </div>
    );
  };

  // Newline-delimited string list editor.
  const LineListField = ({ section, field, label, rows = 8, testId }) => (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <Textarea
        value={(siteContent[section]?.[field] || []).join("\n")}
        onChange={(e) => setSiteContent({
          ...siteContent,
          [section]: {
            ...siteContent[section],
            [field]: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean),
          },
        })}
        rows={rows}
        data-testid={testId}
      />
    </div>
  );

  // Image picker + URL field. Triggers upload via parent handler.
  const ImageUploadField = ({ section, field, currentUrl, label }) => (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <div className="flex gap-2">
        <Input
          value={currentUrl || ""}
          onChange={(e) => setSiteContent({
            ...siteContent,
            [section]: { ...siteContent[section], [field]: e.target.value },
          })}
          placeholder="Image URL or upload below"
          className="flex-1"
        />
        <label className="cursor-pointer">
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleCMSImageUpload(e, section, field)}
          />
          <Button type="button" variant="outline" className="whitespace-nowrap" asChild>
            <span><Upload className="w-4 h-4 mr-1" /> Browse</span>
          </Button>
        </label>
      </div>
      {currentUrl && (
        <div className="mt-2">
          <img src={currentUrl} alt="Preview" className="h-20 w-auto rounded border object-cover" />
        </div>
      )}
    </div>
  );

  const SaveButton = ({ section, label, testId }) => {
    const previewPath = SECTION_PUBLIC_PATH[section];
    return (
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => handleUpdateContent(section, siteContent[section])}
          className="bg-navy hover:bg-navy-light"
          data-testid={testId}
        >
          {label}
        </Button>
        {previewPath && (
          <Button
            type="button"
            variant="outline"
            onClick={() => window.open(previewPath, "_blank", "noopener,noreferrer")}
            data-testid={`preview-${section}`}
            title={`Open ${previewPath} in a new tab`}
          >
            <ExternalLink className="w-4 h-4 mr-1" /> Preview
          </Button>
        )}
      </div>
    );
  };

  return (
    <Card className="border-slate-200 mb-6">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Website Content Management</CardTitle>
        <Button onClick={handleSeedDefaultContent} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" /> Load Default Content
        </Button>
      </CardHeader>
      <CardContent>
        <p className="text-slate-600 mb-4">
          Edit the text and images displayed on your website. Changes will appear immediately on the live site.
        </p>

        {/* Preview-as-visitor toolbar */}
        <div className="mb-6 rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="preview-toolbar">
          <div className="flex items-center gap-2 mb-3">
            <ExternalLink className="w-4 h-4 text-navy" />
            <h4 className="font-medium text-navy text-sm">Preview as visitor</h4>
            <span className="text-xs text-slate-500">— opens each public page in a new tab</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {PUBLIC_PAGES.map((page) => (
              <Button
                key={page.path}
                type="button"
                size="sm"
                variant="outline"
                onClick={() => window.open(page.path, "_blank", "noopener,noreferrer")}
                data-testid={`preview-page-${page.path.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "") || "home"}`}
                className="text-xs"
              >
                {page.label}
              </Button>
            ))}
          </div>
        </div>

        {Object.keys(siteContent).length === 0 ? (
          <div className="text-center py-8">
            <p className="text-slate-500 mb-4">No content found. Click &quot;Load Default Content&quot; to get started.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Homepage Hero */}
            {siteContent.homepage_hero && (
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-4">Homepage Hero Section</h3>
                <div className="grid gap-4">
                  <CmsField section="homepage_hero" field="title" label="Title" />
                  <CmsField section="homepage_hero" field="subtitle" label="Subtitle" multiline rows={2} />
                  <div className="grid grid-cols-2 gap-4">
                    <CmsField section="homepage_hero" field="cta_primary" label="Primary Button Text" />
                    <CmsField section="homepage_hero" field="cta_secondary" label="Secondary Button Text" />
                  </div>
                  <ImageUploadField section="homepage_hero" field="image" currentUrl={siteContent.homepage_hero.image} label="Hero Image" />
                  <SaveButton section="homepage_hero" label="Save Hero Section" />
                </div>
              </div>
            )}

            {/* Why Choose Us */}
            {siteContent.homepage_why_choose && (
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-4">Why Choose Us Section</h3>
                <div className="grid gap-4">
                  <div className="grid grid-cols-2 gap-4">
                    <CmsField section="homepage_why_choose" field="badge" label="Badge Text" />
                    <CmsField section="homepage_why_choose" field="experience_years" label="Years Experience" />
                  </div>
                  <CmsField section="homepage_why_choose" field="title" label="Title" />
                  <ImageUploadField section="homepage_why_choose" field="image" currentUrl={siteContent.homepage_why_choose.image} label="Section Image" />
                  <SaveButton section="homepage_why_choose" label="Save Why Choose Us" />
                </div>
              </div>
            )}

            {/* Homepage — Zoopla Rated Strip */}
            {siteContent.homepage_zoopla && (
              <div className="border rounded-lg p-4" data-testid="cms-homepage-zoopla">
                <h3 className="font-semibold mb-1">Homepage &mdash; Zoopla Rated Strip</h3>
                <p className="text-xs text-slate-500 mb-4">
                  Small &quot;Rated on Zoopla&quot; band shown just under the hero. Enter your current rating and review count from Zoopla, then toggle it on.
                </p>
                <div className="grid gap-4">
                  <label className="flex items-center gap-2 text-sm font-medium text-navy">
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-navy"
                      checked={!!siteContent.homepage_zoopla.enabled}
                      onChange={(e) =>
                        setSiteContent({
                          ...siteContent,
                          homepage_zoopla: {
                            ...siteContent.homepage_zoopla,
                            enabled: e.target.checked,
                          },
                        })
                      }
                      data-testid="cms-homepage-zoopla-enabled"
                    />
                    Show the Zoopla rated strip on the homepage
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <CmsField section="homepage_zoopla" field="rating" label="Star rating (e.g. 4.9)" placeholder="4.9" />
                    <CmsField section="homepage_zoopla" field="review_count" label="Review count (e.g. 42)" placeholder="42" />
                  </div>
                  <CmsField section="homepage_zoopla" field="eyebrow" label="Eyebrow (small caps text)" />
                  <CmsField section="homepage_zoopla" field="headline" label="Headline" />
                  <CmsField section="homepage_zoopla" field="subtext" label="Subtext (optional)" multiline rows={2} />
                  <CmsField section="homepage_zoopla" field="cta_text" label="CTA button text" />
                  <SaveButton section="homepage_zoopla" label="Save Zoopla Strip" testId="cms-homepage-zoopla-save" />
                </div>
              </div>
            )}

            {/* Contact Info */}
            {siteContent.contact_info && (
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-4">Contact Information</h3>
                <div className="grid gap-4">
                  <div className="grid grid-cols-2 gap-4">
                    <CmsField section="contact_info" field="phone" label="Phone" />
                    <CmsField section="contact_info" field="email" label="Email" />
                  </div>
                  <CmsField section="contact_info" field="address" label="Address" />
                  <CmsField section="contact_info" field="service_area" label="Service Area" />
                  <SaveButton section="contact_info" label="Save Contact Info" />
                </div>
              </div>
            )}

            {/* Homepage CTA */}
            {siteContent.homepage_cta && (
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-4">Homepage Call-to-Action Section</h3>
                <div className="grid gap-4">
                  <CmsField section="homepage_cta" field="title" label="Title" />
                  <CmsField section="homepage_cta" field="description" label="Description" multiline rows={2} />
                  <div className="grid grid-cols-2 gap-4">
                    <CmsField section="homepage_cta" field="cta_text" label="Button Text" />
                    <CmsField section="homepage_cta" field="phone" label="Phone Number" />
                  </div>
                  <SaveButton section="homepage_cta" label="Save CTA Section" />
                </div>
              </div>
            )}

            {/* About Page */}
            {siteContent.about_page && (
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-4">About Page</h3>
                <div className="grid gap-4">
                  <div className="grid grid-cols-2 gap-4">
                    <CmsField section="about_page" field="hero_title" label="Hero Title" />
                    <CmsField section="about_page" field="hero_subtitle" label="Hero Subtitle" />
                  </div>
                  <CmsField section="about_page" field="intro_title" label="Intro Title" />
                  <CmsField section="about_page" field="intro_content" label="Intro Content" multiline rows={3} />
                  <CmsField section="about_page" field="mission_content" label="Mission Content" multiline rows={2} />
                  <ImageUploadField section="about_page" field="image" currentUrl={siteContent.about_page.image} label="About Page Image" />
                  <SaveButton section="about_page" label="Save About Page" />
                </div>
              </div>
            )}

            {/* Services - Property Management */}
            {siteContent.services_property_management && (
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-4">Services: Property Management</h3>
                <div className="grid gap-4">
                  <div className="grid grid-cols-2 gap-4">
                    <CmsField section="services_property_management" field="title" label="Title" />
                    <CmsField section="services_property_management" field="subtitle" label="Subtitle" />
                  </div>
                  <CmsField section="services_property_management" field="description" label="Description" multiline rows={2} />
                  <ImageUploadField section="services_property_management" field="image" currentUrl={siteContent.services_property_management.image} label="Property Management Image" />

                  <div className="grid sm:grid-cols-2 gap-4">
                    <CmsField section="services_property_management" field="hero_eyebrow" label="Hero Eyebrow" />
                    <CmsField section="services_property_management" field="hero_cta_text" label="Hero CTA Button" />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <CmsField section="services_property_management" field="services_section_title" label={`"What's Included" Section Title`} />
                    <CmsField section="services_property_management" field="services_section_subtitle" label="Section Subtitle" />
                  </div>
                  <JsonListField
                    section="services_property_management"
                    field="services_items"
                    label={`Services Items (JSON array of {icon, title, description})`}
                    hint="Icons: Users, FileText, Building2, Wrench, Shield, ChartBar, BarChart3, Clock, Home, Briefcase, HandCoins, Award, TrendingUp"
                  />

                  <CmsField section="services_property_management" field="benefits_section_title" label="Benefits Section Title" />
                  <LineListField section="services_property_management" field="benefits_items" label="Benefits Items (one per line)" />

                  <div className="grid sm:grid-cols-2 gap-4">
                    <CmsField section="services_property_management" field="cta_title" label="CTA Title" />
                    <CmsField section="services_property_management" field="cta_button_text" label="CTA Button" />
                  </div>
                  <CmsField section="services_property_management" field="cta_description" label="CTA Description" multiline rows={2} />
                  <CmsField section="services_property_management" field="cta_phone" label="CTA Phone" />

                  <SaveButton section="services_property_management" label="Save Property Management" testId="pm-save" />
                </div>
              </div>
            )}

            {/* Services - Lettings */}
            {siteContent.services_lettings && (
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-4">Services: Lettings</h3>
                <div className="grid gap-4">
                  <div className="grid grid-cols-2 gap-4">
                    <CmsField section="services_lettings" field="title" label="Title" />
                    <CmsField section="services_lettings" field="subtitle" label="Subtitle" />
                  </div>
                  <CmsField section="services_lettings" field="description" label="Description" multiline rows={2} />
                  <ImageUploadField section="services_lettings" field="image" currentUrl={siteContent.services_lettings.image} label="Lettings Image" />

                  <div className="grid sm:grid-cols-2 gap-4">
                    <CmsField section="services_lettings" field="hero_eyebrow" label="Hero Eyebrow" />
                    <CmsField section="services_lettings" field="hero_cta_text" label="Hero CTA Button" />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <CmsField section="services_lettings" field="process_section_title" label="Process Section Title" />
                    <CmsField section="services_lettings" field="process_section_subtitle" label="Process Section Subtitle" />
                  </div>
                  <JsonListField
                    section="services_lettings"
                    field="process_items"
                    label={`Process Items (JSON array of {step, icon, title, description})`}
                    hint="Icons: Search, Camera, Users, FileCheck, Key, FileText, Wrench, Shield"
                    rows={12}
                  />

                  <div className="grid sm:grid-cols-2 gap-4">
                    <CmsField section="services_lettings" field="features_section_title" label="Features Section Title" />
                    <CmsField section="services_lettings" field="features_cta_text" label="Features Section CTA Button" />
                  </div>
                  <LineListField section="services_lettings" field="features_items" label="Features Items (one per line)" />

                  <div className="grid sm:grid-cols-2 gap-4">
                    <CmsField section="services_lettings" field="cta_title" label="CTA Title" />
                    <CmsField section="services_lettings" field="cta_button_text" label="CTA Button" />
                  </div>
                  <CmsField section="services_lettings" field="cta_description" label="CTA Description" multiline rows={2} />
                  <CmsField section="services_lettings" field="cta_phone" label="CTA Phone" />

                  <SaveButton section="services_lettings" label="Save Lettings" testId="lettings-save" />
                </div>
              </div>
            )}

            {/* Services - Investments */}
            {siteContent.services_investments && (
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-4">Services: Investments</h3>
                <div className="grid gap-4">
                  <div className="grid grid-cols-2 gap-4">
                    <CmsField section="services_investments" field="title" label="Title" />
                    <CmsField section="services_investments" field="subtitle" label="Subtitle" />
                  </div>
                  <CmsField section="services_investments" field="description" label="Description" multiline rows={2} />
                  <ImageUploadField section="services_investments" field="image" currentUrl={siteContent.services_investments.image} label="Investments Image" />

                  <div className="grid sm:grid-cols-2 gap-4">
                    <CmsField section="services_investments" field="hero_eyebrow" label="Hero Eyebrow" />
                    <CmsField section="services_investments" field="hero_cta_text" label="Hero CTA Button" />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <CmsField section="services_investments" field="services_section_title" label="Services Section Title" />
                    <CmsField section="services_investments" field="services_section_subtitle" label="Services Section Subtitle" />
                  </div>
                  <JsonListField
                    section="services_investments"
                    field="services_items"
                    label={`Services Items (JSON array of {icon, title, description})`}
                    hint="Icons: MapPin, PoundSterling, BarChart3, TrendingUp, Building2, Users, Home, Award, HandCoins"
                  />

                  <div className="grid sm:grid-cols-2 gap-4">
                    <CmsField section="services_investments" field="areas_section_title" label="Areas Section Title" />
                    <CmsField section="services_investments" field="areas_section_subtitle" label="Areas Section Subtitle" />
                  </div>
                  <JsonListField
                    section="services_investments"
                    field="areas_items"
                    label={`Areas Items (JSON array of {name, description, highlights: [...]})`}
                    rows={14}
                  />

                  <div className="grid sm:grid-cols-2 gap-4">
                    <CmsField section="services_investments" field="cta_title" label="CTA Title" />
                    <CmsField section="services_investments" field="cta_button_text" label="CTA Button" />
                  </div>
                  <CmsField section="services_investments" field="cta_description" label="CTA Description" multiline rows={2} />
                  <CmsField section="services_investments" field="cta_phone" label="CTA Phone" />

                  <SaveButton section="services_investments" label="Save Investments" testId="investments-save" />
                </div>
              </div>
            )}

            {/* Landlords Page */}
            {siteContent.landlords_page && (
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-4">Landlords Page</h3>
                <div className="grid gap-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <CmsField section="landlords_page" field="hero_eyebrow" label="Hero Eyebrow" />
                    <CmsField section="landlords_page" field="hero_cta_text" label="Hero CTA Button Text" />
                  </div>
                  <CmsField section="landlords_page" field="hero_title" label="Hero Title" />
                  <CmsField section="landlords_page" field="hero_subtitle" label="Hero Subtitle" multiline rows={3} />

                  <CmsField section="landlords_page" field="what_we_do_title" label={`"What We Do" Section Title`} />
                  <ImageUploadField section="landlords_page" field="what_we_do_image" currentUrl={siteContent.landlords_page.what_we_do_image} label="What We Do — Image" />
                  <LineListField section="landlords_page" field="what_we_do_items" label="What We Do — Items (one per line)" testId="lp-what-we-do-items" />

                  <CmsField section="landlords_page" field="perfect_for_title" label={`"Perfect For" Section Title`} />
                  <JsonListField
                    section="landlords_page"
                    field="perfect_for_items"
                    label={`"Perfect For" Cards (JSON array of {icon, title, description})`}
                    hint="Icon options: Users, Clock, BarChart3, Shield, FileText, Wrench, Building2, Briefcase, HandCoins, Home, TrendingUp, Award"
                  />

                  <JsonListField
                    section="landlords_page"
                    field="benefits_items"
                    label={`Benefits Cards (JSON array of {icon, title, description})`}
                  />

                  <div className="grid sm:grid-cols-2 gap-4">
                    <CmsField section="landlords_page" field="cta_title" label="CTA Title" />
                    <CmsField section="landlords_page" field="cta_button_text" label="CTA Button Text" />
                  </div>
                  <CmsField section="landlords_page" field="cta_description" label="CTA Description" multiline rows={2} />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <CmsField section="landlords_page" field="cta_phone" label="CTA Phone" />
                    <CmsField section="landlords_page" field="cta_address" label="CTA Address" />
                  </div>

                  <SaveButton section="landlords_page" label="Save Landlords Page" testId="lp-save" />
                </div>
              </div>
            )}

            {/* Tenants Page */}
            {siteContent.tenants_page && (
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-4">Tenants Page</h3>
                <div className="grid gap-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <CmsField section="tenants_page" field="hero_eyebrow" label="Hero Eyebrow" />
                    <CmsField section="tenants_page" field="hero_cta_text" label="Hero CTA Button Text" />
                  </div>
                  <CmsField section="tenants_page" field="hero_title" label="Hero Title" />
                  <CmsField section="tenants_page" field="hero_subtitle" label="Hero Subtitle" multiline rows={3} />

                  <JsonListField
                    section="tenants_page"
                    field="tenant_info_items"
                    label={`Tenant Info Items (JSON array of {icon, title, description})`}
                    hint="Icon options: CheckCircle2, Wrench, Phone, Shield, Home, Users, Building2"
                  />

                  <CmsField section="tenants_page" field="form_section_title" label="Form Section Title" />
                  <CmsField section="tenants_page" field="form_section_description" label="Form Section Description" multiline rows={2} />

                  <CmsField section="tenants_page" field="urgency_guide_title" label="Urgency Guide Title" />
                  <CmsField section="tenants_page" field="urgency_guide_intro" label="Urgency Guide Intro" multiline rows={2} />
                  <JsonListField
                    section="tenants_page"
                    field="urgency_items"
                    label={`Urgency Items (JSON array of {level, title, description})`}
                    hint="Levels: emergency, urgent, routine (these match the form values)"
                  />

                  <CmsField section="tenants_page" field="emergency_card_title" label="Emergency Card Title" />
                  <CmsField section="tenants_page" field="emergency_card_text" label="Emergency Card Text" multiline rows={2} />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <CmsField section="tenants_page" field="emergency_phone" label="Emergency Phone" />
                    <CmsField section="tenants_page" field="office_phone" label="Office Phone" />
                  </div>

                  <SaveButton section="tenants_page" label="Save Tenants Page" testId="tp-save" />
                </div>
              </div>
            )}

            {/* Footer */}
            {siteContent.footer && (
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-4">Footer</h3>
                <div className="grid gap-4">
                  <CmsField section="footer" field="tagline" label="Tagline" />
                  <CmsField section="footer" field="description" label="Description" multiline rows={2} />
                  <CmsField section="footer" field="address" label="Footer Address (shown in top bar, mobile menu & footer)" placeholder="Keystone Properties, 17 Commercial Street, Batley, WF17 5HJ" />
                  <div>
                    <label className="block text-sm font-medium mb-1">Areas We Serve (comma-separated)</label>
                    <Input
                      value={(siteContent.footer.areas || []).join(", ")}
                      onChange={(e) => setSiteContent({
                        ...siteContent,
                        footer: { ...siteContent.footer, areas: e.target.value.split(",").map((a) => a.trim()) },
                      })}
                    />
                  </div>
                  <CmsField section="footer" field="copyright" label="Copyright Text" />
                  <SaveButton section="footer" label="Save Footer" />
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default CmsEditor;
